<template>
  <div class="space-y-6 lg:space-y-6">
    <!-- Header -->
    <div class="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
      <div class="flex-1">
        <h2 class="text-xl font-semibold text-slate-900 dark:text-slate-100">{{ $t('BUSINESS_DATA.WEB_SOURCES.TITLE') }}</h2>
        <p class="text-sm text-slate-500 dark:text-slate-400 mt-1">{{ $t('BUSINESS_DATA.WEB_SOURCES.DESCRIPTION') }}</p>
      </div>
      
      <!-- Mobile: Full width button -->
      <woot-button
        class="!bg-grass-500 !text-white w-full xl:w-auto"
        size="small"
        @click="$emit('add-web-source')"
      >
        <fluent-icon icon="add" size="16" class="mr-2" />
        {{ $t('BUSINESS_DATA.WEB_SOURCES.ADD_NEW') }}
      </woot-button>
    </div>
    
    <!-- Search and Filters -->
    <div class="flex flex-col xl:flex-row gap-4">
      <!-- Search Input -->
      <div class="flex-1 relative">
        <div class="relative">
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="`🔍 ${$t('BUSINESS_DATA.WEB_SOURCES.SEARCH_PLACEHOLDER')}`"
            class="w-full pl-4 pr-10 py-3 text-sm border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-hichat-500/20 focus:border-hichat-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 transition-all duration-200 shadow-sm hover:shadow-md placeholder:text-slate-400"
            @input="onSearch"
          />
          <div v-if="isSearching" class="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div class="w-4 h-4 border-2 border-slate-200 dark:border-slate-600 border-t-hichat-500 rounded-full animate-spin"></div>
          </div>
        </div>
      </div>
      
      <!-- Filter Controls -->
      <div class="flex flex-col gap-3 xl:flex-row xl:flex-wrap">
        <!-- Status Filter -->
        <div class="relative w-full xl:w-auto xl:flex-1">
          <fluent-icon icon="circle" size="16" class="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-slate-500 pointer-events-none z-10" />
          <select 
            v-model="selectedStatus" 
            class="pl-10 pr-10 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-hichat-500/20 focus:border-hichat-500 w-full xl:w-40 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 transition-all duration-200 shadow-sm hover:shadow-md cursor-pointer"
            style="-webkit-appearance: none; -moz-appearance: none; appearance: none; background-image: none;"
            @change="onFilterChange"
          >
            <option value="">{{ $t('BUSINESS_DATA.WEB_SOURCES.ALL_STATUS') }}</option>
            <option value="active">{{ $t('BUSINESS_DATA.WEB_SOURCES.ACTIVE') }}</option>
            <option value="paused">{{ $t('BUSINESS_DATA.WEB_SOURCES.PAUSED') }}</option>
            <option value="error">{{ $t('BUSINESS_DATA.WEB_SOURCES.ERROR') }}</option>
          </select>
      
        </div>
        
        <!-- Type Filter -->
        <div class="relative w-full xl:w-auto xl:flex-1">
          <select 
            v-model="selectedType" 
            class="pl-10 pr-10 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-hichat-500/20 focus:border-hichat-500 w-full xl:w-48 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 transition-all duration-200 shadow-sm hover:shadow-md cursor-pointer"
            style="padding-left: 2.5rem; -webkit-appearance: none; -moz-appearance: none; appearance: none; background-image: none;"
            @change="onFilterChange"
          >
            <option value="">{{ $t('BUSINESS_DATA.WEB_SOURCES.ALL_TYPES') }}</option>
            <option value="website">{{ $t('BUSINESS_DATA.WEB_SOURCES.URL') }}</option>
            <option value="documentation">{{ $t('BUSINESS_DATA.WEB_SOURCES.SITEMAP') }}</option>
            <option value="blog">{{ $t('BUSINESS_DATA.WEB_SOURCES.RSS') }}</option>
          </select>
      
        </div>
      </div>
    </div>
    
    <!-- Content Area -->
    <div>
      <!-- Loading State -->
      <div v-if="isLoading" class="py-8 flex items-center justify-center">
        <Spinner size="md" color-scheme="primary" />
      </div>
      
      <!-- Empty State -->
      <div v-else-if="webSources.length === 0" class="text-center py-12">
        <div class="bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm rounded-2xl p-8 border border-white/30 dark:border-slate-600/30 max-w-md mx-auto">
          <fluent-icon icon="globe" size="48" class="text-slate-300 dark:text-slate-600 mx-auto mb-4" />
          <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">{{ $t('BUSINESS_DATA.WEB_SOURCES.EMPTY_TITLE') }}</h3>
          <p class="text-sm text-slate-500 dark:text-slate-400 mb-6">{{ $t('BUSINESS_DATA.WEB_SOURCES.EMPTY_DESCRIPTION') }}</p>
          <woot-button @click="$emit('add-web-source')" variant="smooth" class="input-base">
            {{ $t('BUSINESS_DATA.WEB_SOURCES.ADD_FIRST_SOURCE') }}
          </woot-button>
        </div>
      </div>
      
      <!-- Web Sources List -->
      <div v-else class="space-y-4">
        <div 
          v-for="source in webSources" 
          :key="source.id" 
          class="group bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm rounded-md p-2 border border-n-gray-1 dark:border-slate-600/30 hover:bg-white/80 dark:hover:bg-slate-800/80 hover:border-blue-200/50 dark:hover:border-blue-500/30 hover:shadow-lg transition-all duration-300"
        >
          <div class="flex items-start gap-2 sm:gap-3">
            <div class="flex-1 min-w-0">
              <div class="flex flex-wrap items-center gap-2 mb-2">
                <h4 class="text-base font-semibold text-slate-900 dark:text-slate-100 break-words">{{ source.title || source.url }}</h4>
                <div class="flex items-center gap-2 flex-shrink-0">
                  <span 
                    class="px-2 py-0.5 rounded-lg text-xs font-medium"
                    :class="{
                      'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400': source.status === 'active',
                      'bg-slate-50 dark:bg-slate-700 text-slate-600 dark:text-slate-300': source.status === 'paused',
                      'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400': source.status === 'error'
                    }"
                  >
                    {{ $t(`BUSINESS_DATA.WEB_SOURCES.${source.status.toUpperCase()}`) }}
                  </span>
                  <span class="px-2 py-0.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg text-xs font-medium">
                    {{ $t(`BUSINESS_DATA.WEB_SOURCES.${(source.content_type || '').toUpperCase()}`) }}
                  </span>
                </div>
              </div>
              
              <p class="text-slate-600 dark:text-slate-300 mb-2 font-mono text-sm break-all">{{ source.url }}</p>
              
              <!-- Progress Bar for Active Sources -->
              <div v-if="source.status === 'active' && source.sync_progress" class="mb-2">
                <div class="flex items-center gap-2">
                  <div class="flex-1 h-1 bg-slate-200 dark:bg-slate-600 rounded-full overflow-hidden">
                    <div 
                      class="h-full bg-gradient-to-r from-hichat-500 to-hichat-600 transition-all duration-300"
                      :style="{ width: `${source.sync_progress}%` }"
                    ></div>
                  </div>
                  <span class="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">{{ source.sync_progress }}% {{ $t('BUSINESS_DATA.WEB_SOURCES.SYNCED') }}</span>
                </div>
              </div>
              
              <!-- Error Message -->
              <div v-if="source.status === 'error' && source.error_message" class="mb-2 flex items-center gap-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 rounded text-red-600 dark:text-red-400 text-sm">
                <fluent-icon icon="warning" size="14" />
                <span>{{ source.error_message }}</span>
              </div>
              
              <div class="mt-3 flex flex-wrap items-center justify-between gap-2">
                <div class="flex flex-wrap items-center gap-3 text-xs text-slate-400">
                  <span class="font-mono">ID: {{ source.id }}</span>
                  <span>{{ formatDate(source.created_at) }}</span>
                  <span v-if="source.last_synced_at" class="text-slate-400">
                    {{ $t('BUSINESS_DATA.WEB_SOURCES.LAST_SYNCED') }}: {{ formatDate(source.last_synced_at) }}
                  </span>
                  <span v-if="source.pages_count" class="text-slate-400">
                    {{ source.pages_count }} {{ $t('BUSINESS_DATA.WEB_SOURCES.PAGES') }}
                  </span>
                </div>

                <div class="flex items-center gap-1 xl:opacity-0 xl:group-hover:opacity-100 transition-opacity">
                  <woot-button
                    variant="smooth"
                    size="small"
                    icon="edit"
                    color-scheme="secondary"
                    v-tooltip.top="$t('GENERAL.TOOLTIPS.EDIT')"
                    @click="$emit('edit-web-source', source)"
                  />
                  
                  <woot-button
                    v-if="source.status === 'active'"
                    variant="smooth"
                    size="small"
                    color-scheme="secondary"
                    class-names="button--only-icon flex items-center justify-center !p-0"
                    v-tooltip.top="$t('BUSINESS_DATA.WEB_SOURCES.PAUSE')"
                    @click="pauseSource(source)"
                  >
                    <span class="flex items-center justify-center w-full h-full">
                      <LucideIcon name="pause-circle" :size="18" :stroke-width="2.2" />
                    </span>
                  </woot-button>
                  
                  <woot-button
                    v-else-if="source.status === 'paused'"
                    variant="smooth"
                    size="small"
                    color-scheme="secondary"
                    class-names="button--only-icon flex items-center justify-center !p-0"
                    v-tooltip.top="$t('BUSINESS_DATA.WEB_SOURCES.RESUME')"
                    @click="resumeSource(source)"
                  >
                    <span class="flex items-center justify-center w-full h-full">
                      <LucideIcon name="play-circle" :size="18" :stroke-width="2.2" />
                    </span>
                  </woot-button>
                  
                  <woot-button
                    variant="smooth"
                    size="small"
                    color-scheme="secondary"
                    class-names="button--only-icon flex items-center justify-center !p-0"
                    v-tooltip.top="$t('BUSINESS_DATA.WEB_SOURCES.SYNC')"
                    @click="syncSource(source)"
                  >
                    <span class="flex items-center justify-center w-full h-full">
                      <LucideIcon name="refresh-ccw" :size="18" :stroke-width="2.2" />
                    </span>
                  </woot-button>
                  
                  <woot-button
                    variant="smooth"
                    size="small"
                    icon="dismiss-circle"
                    color-scheme="alert"
                    v-tooltip.top="$t('GENERAL.TOOLTIPS.DELETE')"
                    @click="deleteWebSource(source)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Pagination -->
    <TableFooter
      v-if="!isLoading && webSources.length > 0 && pagination.total_pages > 1"
      :current-page="pagination.current_page"
      :total-count="pagination.total_count"
      :page-size="pagination.per_page"
      class="border-slate-50 dark:border-slate-800 border-t !px-0 py-4"
      @page-change="goToPage"
    />
  </div>
</template>

<script>
import Spinner from 'shared/components/Spinner.vue';
import { useAlert } from 'dashboard/composables'
import LucideIcon from 'dashboard/components/LucideIcon.vue'
import { formatDateTimeLocalized } from 'shared/helpers/DateHelper';
import TableFooter from 'dashboard/components/widgets/TableFooter.vue';

export default {
  name: 'WebSourcesTab',
  
  components: {
    LucideIcon,
    Spinner,
    TableFooter
  },
  
  props: {
    webSources: {
      type: Array,
      default: () => []
    },
    pagination: {
      type: Object,
      default: () => ({
        current_page: 1,
        per_page: 20,
        total_count: 0,
        total_pages: 0,
        has_next_page: false,
        has_prev_page: false
      })
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    isSearching: {
      type: Boolean,
      default: false
    }
  },
  
  emits: ['add-web-source', 'edit-web-source', 'search', 'filter-change', 'page-change', 'refresh'],
  
  data() {
    return {
      searchQuery: '',
      selectedStatus: '',
      selectedType: '',
      searchTimeout: null
    }
  },
  
  computed: {
    currentAccountId() {
      return this.$store.getters.getCurrentAccountId
    },
    
  },
  
  beforeUnmount() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = null
    }
  },
  
  methods: {
    onSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }
      
      this.searchTimeout = setTimeout(() => {
        this.$emit('search', this.searchQuery)
      }, 500)
    },
    
    onFilterChange() {
      this.$emit('filter-change', {
        status: this.selectedStatus,
        content_type: this.selectedType
      })
    },
    
    goToPage(page) {
      if (page >= 1 && page <= this.pagination.total_pages && page !== this.pagination.current_page) {
        this.$emit('page-change', page)
      }
    },
    
    getSourceIcon(type) {
      const icons = {
        website: 'globe',
        documentation: 'sitemap',
        blog: 'rss',
        api_docs: 'book-contacts'
      }
      return icons[type] || 'globe'
    },
    
    async pauseSource(source) {
      try {
        await window.axios.put(`/api/v1/accounts/${this.currentAccountId}/web_content_sources/${source.id}`, {
          web_content_source: { status: 'paused' }
        })
        this.$emit('refresh')
        useAlert(this.$t('BUSINESS_DATA.WEB_SOURCES.PAUSED_SUCCESS'), {
          type: 'success',
        })
      } catch (error) {
        useAlert(this.$t('BUSINESS_DATA.WEB_SOURCES.UPDATE_ERROR'), {
          type: 'error',
        })
      }
    },
    
    async resumeSource(source) {
      try {
        await window.axios.put(`/api/v1/accounts/${this.currentAccountId}/web_content_sources/${source.id}`, {
          web_content_source: { status: 'active' }
        })
        this.$emit('refresh')
        useAlert(this.$t('BUSINESS_DATA.WEB_SOURCES.RESUMED_SUCCESS'), {
          type: 'success',
        })
      } catch (error) {
        useAlert(this.$t('BUSINESS_DATA.WEB_SOURCES.UPDATE_ERROR'), {
          type: 'error',
        })
      }
    },
    
    async syncSource(source) {
      try {
        await window.axios.post(`/api/v1/accounts/${this.currentAccountId}/web_content_sources/${source.id}/crawl`)
        this.$emit('refresh')
        useAlert(this.$t('BUSINESS_DATA.WEB_SOURCES.SYNC_STARTED'), {
          type: 'success',
        })
      } catch (error) {
        useAlert(this.$t('BUSINESS_DATA.WEB_SOURCES.SYNC_ERROR'), {
          type: 'error',
        })
      }
    },
    
    async deleteWebSource(source) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(this.$t('BUSINESS_DATA.WEB_SOURCES.DELETE_CONFIRM'))) {
        return
      }
      
      try {
        await window.axios.delete(`/api/v1/accounts/${this.currentAccountId}/web_content_sources/${source.id}`)
        this.$emit('refresh')
        useAlert(this.$t('BUSINESS_DATA.WEB_SOURCES.DELETED_SUCCESS'), {type: 'success'})
      } catch (error) {
        useAlert(this.$t('BUSINESS_DATA.WEB_SOURCES.DELETE_ERROR'), {type: 'error'})
      }
    },
    
    formatDate(dateString) {
      return formatDateTimeLocalized(dateString, {
        includeSeconds: false,
        localeOverride: this.$i18n?.locale,
      })
    }
  }
}
</script>
